import { Dropdown } from "antd";
import { ChevronDown, LogOut } from "lucide-react";

import { useCurrentUser } from "@/features/auth/model/useAuth";
import { useLogout } from "@/features/auth/model/useLogout";

import { cn } from "@/shared/lib/cn";

function initials(first?: string | null, last?: string | null, email?: string | null): string {
  const a = first?.trim()?.[0]?.toUpperCase() ?? "";
  const b = last?.trim()?.[0]?.toUpperCase() ?? "";
  if (a || b) return `${a}${b}`;
  return email?.trim()?.[0]?.toUpperCase() ?? "?";
}

function displayName(user: {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}): string {
  const full = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  if (full) return full;
  return user.email?.split("@")[0] ?? "Account";
}

function roleLabel(role?: string | null): string | null {
  if (!role) return null;
  return role.replace(/_/g, " ");
}

type AccountMenuProps = {
  className?: string;
  /** Icon-only trigger (mobile top bar). */
  compact?: boolean;
};

/** Top bar account control — profile summary + Log out. */
export function AccountMenu({ className, compact = false }: AccountMenuProps) {
  const user = useCurrentUser();
  const logoutUser = useLogout();

  if (!user) return null;

  const name = displayName(user);
  const initialsText = initials(user.first_name, user.last_name, user.email);
  const role = roleLabel(user.role);

  const menu = (
    <div
      className="w-64 overflow-hidden rounded-xl border border-border/80 bg-card shadow-lg ring-1 ring-black/5"
      role="menu"
    >
      <div className="border-b border-border/60 bg-muted/25 px-4 py-3.5">
        <div className="flex items-center gap-3">
          <span
            className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground"
            aria-hidden
          >
            {initialsText}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground">{name}</p>
            <p className="truncate text-xs text-muted-foreground">{user.email}</p>
            {role ? (
              <span className="mt-1.5 inline-block rounded-full bg-primary/10 px-2 py-0.5 text-3xs font-medium uppercase tracking-wide text-primary">
                {role}
              </span>
            ) : null}
          </div>
        </div>
      </div>
      <div className="p-1.5">
        <button
          type="button"
          role="menuitem"
          onClick={() => logoutUser()}
          className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted/80"
        >
          <span className="flex size-8 items-center justify-center rounded-md bg-muted text-muted-foreground">
            <LogOut className="size-4" aria-hidden />
          </span>
          Log out
        </button>
      </div>
    </div>
  );

  return (
    <Dropdown
      dropdownRender={() => menu}
      trigger={["click"]}
      placement="bottomRight"
      destroyPopupOnHide
    >
      <button
        type="button"
        className={cn(
          "inline-flex max-w-full cursor-pointer items-center rounded-full border border-border/70 bg-background/90 text-foreground shadow-sm transition-[box-shadow,background-color] hover:bg-muted/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
          compact ? "size-9 justify-center p-0" : "gap-2 py-1 pl-1 pr-2.5",
          className,
        )}
        aria-haspopup="menu"
        aria-label={compact ? "Account menu" : `Account menu, ${name}`}
        title={name}
      >
        <span
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground"
          aria-hidden
        >
          {initialsText}
        </span>
        {!compact ? (
          <>
            <span className="hidden min-w-0 flex-col items-start leading-tight sm:flex">
              <span className="max-w-28 truncate text-sm font-medium">{user.first_name ?? name}</span>
              {role ? (
                <span className="max-w-28 truncate text-3xs font-medium uppercase tracking-wide text-muted-foreground">
                  {role}
                </span>
              ) : null}
            </span>
            <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
          </>
        ) : null}
      </button>
    </Dropdown>
  );
}
